import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Upload, LayoutGrid, Images, Home, Settings2, ShieldCheck, UserCog, LogOut, Menu, X } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DashboardTab from "./admin/DashboardTab";
import OrdersTab from "./admin/OrdersTab";
import ProductsTab from "./admin/ProductsTab";
import BulkTab from "./admin/BulkTab";
import CategoriesTab from "./admin/CategoriesTab";
import PortfolioTab from "./admin/PortfolioTab";
import HomepageTab from "./admin/HomepageTab";
import SiteTab from "./admin/SiteTab";
import PoliciesTab from "./admin/PoliciesTab";
import ProfileTab from "./admin/ProfileTab";
import { Seo } from "../components/Seo";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "products", label: "Products", icon: ShoppingBag },
  { key: "bulk", label: "Bulk Upload", icon: Upload },
  { key: "categories", label: "Categories", icon: LayoutGrid },
  { key: "portfolio", label: "Portfolio", icon: Images },
  { key: "homepage", label: "Homepage", icon: Home },
  { key: "site", label: "Site & Payments", icon: Settings2 },
  { key: "policies", label: "Policies & Charges", icon: ShieldCheck },
  { key: "profile", label: "Profile & Security", icon: UserCog },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);

  const reload = useCallback(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/products", { params: { limit: 200 } }).then((r) => setProducts(r.data.items)).catch(() => {});
  }, []);

  useEffect(() => { if (user && user.role === "admin") reload(); }, [user, reload]);

  if (user === null) return <div className="bg-boutique flex min-h-screen items-center justify-center font-display text-xl">Loading…</div>;
  if (!user || user.role !== "admin") { navigate("/admin/login"); return null; }

  const navButton = ({ key, label, icon: Icon }, mobile = false) => (
    <button key={key} data-testid={`admin-tab-${key}`} onClick={() => { setTab(key); setMenuOpen(false); }}
      className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left text-xs tracking-[0.15em] uppercase transition-colors ${tab === key ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
      <Icon size={15} className="shrink-0" /> {label}
    </button>
  );

  return (
    <div className="bg-boutique min-h-screen" data-testid="admin-dashboard">
      <Seo title="Admin — Anaya Fashion & Gifts" path="/admin" noindex />
      {/* mobile top bar */}
      <header className="glass sticky top-0 z-40 lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            {settings?.logo_url && <img src={imgUrl(settings.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover" />}
            <span className="font-display tracking-[0.25em]">ANAYA <span className="text-[10px] text-muted-foreground">ADMIN</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-store" className="rounded-full border border-input px-3 py-1.5 text-[10px] tracking-widest uppercase hover:bg-secondary">Store</Link>
            <button data-testid="admin-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Menu" className="rounded-full p-2 hover:bg-secondary"><Menu size={18} /></button>
          </div>
        </div>
      </header>

      {/* mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm lg:hidden" data-testid="admin-mobile-menu" onClick={() => setMenuOpen(false)}>
          <div className="glass-deep h-full w-72 max-w-[85%] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <span className="font-display tracking-[0.25em]">MENU</span>
              <button onClick={() => setMenuOpen(false)} data-testid="admin-menu-close" aria-label="Close menu" className="rounded-full p-2 hover:bg-secondary"><X size={16} /></button>
            </div>
            <nav className="space-y-1">{TABS.map((t) => navButton(t, true))}</nav>
            <button data-testid="admin-logout-btn" onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="mt-4 flex w-full items-center gap-3 rounded-xl border border-destructive/40 px-4 py-2.5 text-xs tracking-[0.15em] text-destructive uppercase hover:bg-destructive/10">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      )}

      <div className="flex">
        {/* desktop sidebar */}
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-white/60 bg-white/50 p-4 backdrop-blur lg:flex" data-testid="admin-sidebar">
          <div className="mb-6 flex items-center gap-2.5 px-2 pt-2">
            {settings?.logo_url && <img src={imgUrl(settings.logo_url)} alt="" className="h-9 w-9 rounded-full object-cover" />}
            <span className="font-display tracking-[0.22em]">ANAYA <span className="block text-[9px] tracking-[0.3em] text-muted-foreground">ADMIN PANEL</span></span>
          </div>
          <nav className="flex-1 space-y-1 overflow-auto">{TABS.map((t) => navButton(t))}</nav>
          <div className="space-y-2 border-t border-input/60 pt-3">
            <Link to="/" data-testid="admin-view-store-desktop" className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-xs tracking-[0.15em] uppercase hover:bg-secondary">View store</Link>
            <button data-testid="admin-logout-btn-desktop" onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-xs tracking-[0.15em] text-destructive uppercase hover:bg-destructive/10">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {tab === "dashboard" && <DashboardTab onNavigate={setTab} />}
          {tab === "orders" && <OrdersTab />}
          {tab === "products" && <ProductsTab categories={categories} />}
          {tab === "bulk" && <BulkTab products={products} categories={categories} />}
          {tab === "categories" && <CategoriesTab categories={categories} reload={reload} />}
          {tab === "portfolio" && <PortfolioTab />}
          {tab === "homepage" && <HomepageTab settings={settings} products={products} reload={reload} />}
          {tab === "site" && <SiteTab settings={settings} reload={reload} />}
          {tab === "policies" && <PoliciesTab settings={settings} reload={reload} />}
          {tab === "profile" && <ProfileTab />}
        </main>
      </div>
    </div>
  );
}
